import React, { useState, Fragment } from 'react';
import { Link, withRouter, RouteComponentProps } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { UtilityFunctions } from "../utils/UtilityFunctions";
import { STRING_CONSTANTS } from "../utils/constants/stringConstants";
import { userService } from '../_services';
import ToastHeader from "../components/toastHeader";
import ValidationErrorToast from '../components/validationErrorToast';
import SuccessToast from '../components/successToast';
import { useWhitelabel } from "../context/WhitelabelContext"; // Import our new hook! Adjust path as needed

// Define props type
interface ForgotPasswordProps extends RouteComponentProps<any> {}

const ForgotPassword: React.FC<ForgotPasswordProps> = (props) => {
    // --- State Management ---
    const [email, setEmail] = useState(props?.location?.state?.email || "");
    const [btnClick, setBtnClick] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { logoSrc } = useWhitelabel();

    // --- Validation ---
    const isEmailInvalid = !UtilityFunctions.validateEmail(email);
    const isFormValid = !isEmailInvalid;

    // --- Handlers ---
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setBtnClick(true);

        if (UtilityFunctions.isEmptyOrNullOrUndefined(email)) {
            return ValidationErrorToast(STRING_CONSTANTS.formLabelString.label_toast_email, STRING_CONSTANTS.validationString.req_email);
        }
        if (isEmailInvalid) {
            return ValidationErrorToast(STRING_CONSTANTS.formLabelString.label_toast_email, STRING_CONSTANTS.validationString.req_email_incorrect);
        }
        
        setIsLoading(true);
        userService.forgotPassword({ email })
            .then(() => {
                SuccessToast(STRING_CONSTANTS.SUCCESS_MESSAGES.SUCCESS_HEADER, STRING_CONSTANTS.SUCCESS_MESSAGES.EMAIL_SENT_MESSAGE);
                setTimeout(() => {
                    props.history.push(STRING_CONSTANTS.navigationString.login);
                }, 1500); // Slightly longer timeout to allow user to read the success toast
            })
            .catch(function (error) {
                ToastHeader(error);
                setIsLoading(false);
            });
    };

    // --- Navigation Data ---
    const loginLink = { pathname: STRING_CONSTANTS.navigationString.login };
    const onBoardingLink = { pathname: "/", state: { activeIndex: 2 } };

    // --- Styles (Patricia's Consistent & Clean Makeover! 💅) ---
    const styles: { [key: string]: React.CSSProperties } = {
        container: {
            fontFamily: "'DM Sans', sans-serif",
            backgroundColor: "#FFFFFF",
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
        },
        card: {
            background: "#FFFFFF",
            borderRadius: "16px",
            padding: "40px",
            width: "100%",
            maxWidth: "480px",
            border: "1px solid #E0E0E0",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.05)",
            textAlign: "center",
        },
        logo: {
            cursor: "pointer",
            height: "40px",
            marginBottom: "24px",
        },
        title: {
            fontFamily: "'Urbanist', sans-serif",
            fontWeight: 700,
            fontSize: "28px",
            lineHeight: 1.2,
            marginBottom: "8px",
            color: "#141414",
        },
        subtitle: {
            fontSize: "16px",
            color: "#555555",
            marginBottom: "32px",
        },
        formGroup: {
            marginBottom: "24px",
            position: "relative",
            textAlign: "left",
        },
        label: {
            display: 'block',
            fontWeight: 500,
            fontSize: '14px',
            color: '#242424',
            marginBottom: '8px',
        },
        input: {
            width: "100%",
            padding: "12px 16px",
            backgroundColor: "#F8F9FA",
            border: "1px solid #D1D1D1",
            borderRadius: "8px",
            color: "#242424",
            fontSize: "16px",
            outline: "none",
            transition: "border-color 0.3s ease, box-shadow 0.3s ease",
        },
        inputError: {
            borderColor: "#dc3545",
        },
        button: {
            background: "#0065FF",
            borderRadius: "8px",
            border: "none",
            boxShadow: "0px 10px 20px -10px rgba(0, 101, 255, 0.4)",
            padding: "16px",
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 700,
            fontSize: "16px",
            color: "#FFFFFF",
            cursor: "pointer",
            width: "100%",
            transition: "all 0.3s ease",
        },
        buttonDisabled: {
            opacity: 0.6,
            cursor: "not-allowed",
        },
        footerText: {
            marginTop: "24px",
            fontSize: "14px",
            color: "#555555"
        },
        footerLink: {
            color: "#0065FF",
            fontWeight: 700,
            textDecoration: "none",
        }
    };

    return (
        <Fragment>
            <Helmet>
                <title>Forgot Password</title>
                <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Urbanist:wght@400;500;700&display=swap" rel="stylesheet" />
            </Helmet>
            <div style={styles.container}>
                <div style={styles.card}>
                    <div style={styles.logo}>
                        {logoSrc && <img src={logoSrc} style={styles.logo} alt="logo" />}
                    </div>
                    <h1 style={styles.title}>Forgot Your Password?</h1>
                    <p style={styles.subtitle}>No worries! Enter your email below and we'll send you a reset link.</p>

                    <form onSubmit={handleSubmit} noValidate>
                        <div style={styles.formGroup}>
                            <label htmlFor="email" style={styles.label}>Email Address</label>
                            <input 
                                type="email" 
                                id="email"
                                placeholder="you@company.com" 
                                style={{
                                    ...styles.input, 
                                    ...(btnClick && isEmailInvalid ? styles.inputError : {})
                                }}
                                value={email} 
                                onChange={(e) => { setEmail(e.target.value); setBtnClick(false); }} 
                                disabled={isLoading}
                            />
                        </div>

                        <button 
                            type='submit' 
                            style={{...styles.button, ...((!isFormValid || isLoading) ? styles.buttonDisabled : {})}}
                            disabled={!isFormValid || isLoading}
                        >
                            {isLoading ? 'Sending Link...' : 'Send Password Reset Link'}
                        </button>
                    </form>

                    <p style={styles.footerText}>
                        Remembered your password?{' '}
                        <Link to={loginLink} style={styles.footerLink}>
                            Back to Sign In
                        </Link>
                    </p>
                </div>
            </div>
        </Fragment>
    );
};

export default withRouter(ForgotPassword);